import { useCallback } from 'react'
import { Currency, useCurrenciesQuery } from '../api/currency'
import { useIntlContext } from '../components/IntlProviders.context'

const getLocaleCurrencyFormat = (locale: string, currency: string | undefined) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    maximumFractionDigits: 20, // 20 is the max allowed value
    roundingMode: 'halfEven'
  })
}

const getLocaleBasicNumberFormat = (locale: string) => {
  return Intl.NumberFormat(locale, {
    maximumFractionDigits: 20, // 20 is the max allowed value
    roundingMode: 'halfEven'
  })
}

/**
 * useFormatCurrencyLocale
 * This hook will return a currency formatting function based on the user's locale.
 */
export const useFormatCurrencyLocale = () => {
  const intl = useIntlContext().intl
  const currenciesQuery = useCurrenciesQuery()

  /**
   * Formats currency based on the user's locale. By default, the currency code
   * will be displayed otherwise the currency will be formatted based on
   * the locale's ISO (4217) standard
   * @param amount (number | string) Number to format as a currency
   * @param opts {Object} The options object
   * @param opts.currency {string} The currency code
   * @returns {string} A string representing the formatted currency
   */
  return useCallback(
    (
      amount: number | string | undefined,
      { currency, maxDecimals }: { currency?: string; maxDecimals?: number } = {}
    ): string => {
      let n = Number(amount)
      if (amount === undefined || Number.isNaN(n)) {
        n = 0
      }
      const currencyData = currenciesQuery.data?.find((c: Currency) => c.code === currency)
      const maximumFractionDigits = maxDecimals
        ? Math.max(maxDecimals, currencyData?.decimal_places ?? 0)
        : currencyData?.decimal_places ?? undefined
      if (currency) {
        try {
          return new Intl.NumberFormat(intl.locale, {
            style: 'currency',
            currency,
            currencyDisplay: 'code',
            maximumFractionDigits,
            roundingMode: 'halfEven'
          }).format(n)
        } catch (e) {
          // If the currency code is invalid it will throw a RangeError, so we allow
          // that error (aka don't rethrow it) and let the function continue to the
          // Number format fallback below. Otherwise, it's an error we didn't expect,
          // so we rethrow it.
          if (!(e instanceof RangeError)) throw e
        }
      }
      return new Intl.NumberFormat(intl.locale, {
        maximumFractionDigits: maximumFractionDigits ?? 2,
        roundingMode: 'halfEven'
      }).format(n)
    },
    [intl, currenciesQuery.data]
  )
}

/**
 * useFormatCurrencyRangeLocale
 * This hook will return a currency range formatting function based on the user's locale.
 */
export const useFormatCurrencyRangeLocale = () => {
  const intl = useIntlContext().intl

  /**
   * Formats currency range based on the user's locale. By default, the currency code
   * will be displayed otherwise the currency range will be formatted based on
   * the locale's ISO (4217) standard
   * @param minAmount (number | string) minimum Amount to format as a currency
   * @param maxAmount (number | string) maximum Amount to format as a currency
   * @param opts {Object} The options object
   * @param opts.currency {string} The currency code
   * @returns {string} A string representing the formatted currency range
   */
  return useCallback(
    (
      minAmount: number | string | undefined,
      maxAmount: number | string | undefined,
      { currency }: { currency?: string } = {}
    ): string => {
      let min = Number(minAmount)
      if (minAmount === undefined || Number.isNaN(min)) {
        min = 0
      }
      let max = Number(maxAmount)
      if (maxAmount === undefined || Number.isNaN(max)) {
        max = 0
      }
      if (currency) {
        try {
          return getLocaleCurrencyFormat(intl.locale, currency).formatRange(min, max)
        } catch (e) {
          // If the currency code is invalid it will throw a RangeError, so we allow
          // that error (aka don't rethrow it) and let the function continue to the
          // Number format fallback below. Otherwise it's an error we didn't expect,
          // so we rethrow it.
          if (!(e instanceof RangeError)) throw e
        }
      }
      return getLocaleBasicNumberFormat(intl.locale).formatRange(min, max)
    },
    [intl]
  )
}

/**
 * Formats a currency to two decimal places.
 *
 * @deprecated Use useFormatCurrencyLocale instead of this util function. Please reference our Storybook page on currency.
 */
export const formatCurrency = (x?: number | string): string => {
  const n = Number(x)
  if (x === undefined || Number.isNaN(n)) {
    return formatCurrency(0)
  }
  return n.toFixed(2)
}

/**
 * @deprecated Only localized formats are now recommended. Use formatCurrencyLocale instead of
 * this util function. Please reference our Storybook page on currency:
 * http://storybook.beta.vndly.com/?path=/docs/components-inputs-currency--docs
 */
export const currencyCodeToSymbol = (currency?: string) => {
  /*
  params: currency: a string on length 3 that represents currency code. Example: USD, INR, EUR
  returns: string of len 1 - the symbol of the given currency code. Example: $, ₹, €

  Note: We should strongly consider deprecating this method. The code is an ISO standard (4217) and is locale agnostic
   */
  if (currency)
    return (
      new Intl.NumberFormat('en-US', { style: 'currency', currency })
        .formatToParts()
        .find(({ type }) => type === 'currency')?.value ?? null
    )
  return null
}
